import React, { useEffect, useState, forwardRef, useRef } from 'react';
import PropTypes from 'prop-types';
import {
  Slide,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Button,
  TextField,
  Grid,
} from '@mui/material';
import { useForm, Controller } from 'react-hook-form';
import validationRules, {
  maxLengthValidation,
} from '../../utils/validations.util';
import Iconify from '../common/iconify.component';
import Loading from '../table-elements/loading.component';
import SnackbarInfo from '../common/snackbar-info.component';
import {
  createNewChannel,
  viewChannelById,
  updateChannelById,
} from '../../services/content.service';
import Status from '../form-elements/status.component';
import {
  CONTENT_ALIAS,
} from '../../config/const.config';
import ConfirmPopup from '../common/confirm-popup.component';

const Transition = forwardRef((props, ref) => (
  <Slide direction="up" ref={ref} {...props} />
));

const AddEditCategories = ({ title, dataId, onCancel, onSuccess }) => {
  const formData = useRef(null);
  const channelData = useRef(null);
  const confirmationMessage = useRef('');
  const {
    control,
    formState: { errors },
    handleSubmit,
    setValue,
  } = useForm({
    defaultValues: {
      title: '',
      status: null,
      description: '',
      channelCode: ''
    },
  });

  const [loading, setLoading] = useState(true);
  const [formSubmitted, setFormSubmitted] = useState(false);
  const [snackbarInfo, setSnackbarInfo] = useState({
    show: false,
    type: '',
    message: '',
  });
  const [isConfirmationPromptOpen, setIsConfirmationPromptOpen] =
    useState(false);

  const createRandomString = (length)  => {
    const chars = "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789";
    let result = "";
    const randomArray = new Uint8Array(length);
    crypto.getRandomValues(randomArray);
    randomArray.forEach((number) => {
      result += chars[number % chars.length];
    });
    return result;
  }
    
  const saveNewData = (payload) => {
    createNewChannel(payload)
      .then(() => {
        onSuccess(`New ${CONTENT_ALIAS.CHANNEL_SINGULAR} added successfully.`);
        formData.current = null;
      })
      .catch((err) => {
        const message =
          err.response?.data?.message ||
          'Something went wrong, please try again.';

        setSnackbarInfo({
          ...snackbarInfo,
          show: true,
          type: 'error',
          message,
        });
        setFormSubmitted(false);
        formData.current = null;
      });
  };

  const updateExistingData = (payload) => {
    updateChannelById(dataId, payload)
      .then(() => {
        onSuccess(
          `${CONTENT_ALIAS.CHANNEL_SINGULAR} details updated successfully.`
        );
        formData.current = null;
      })
      .catch((err) => {
        const message =
          err.response?.data?.message ||
          'Something went wrong, please try again.';

        setSnackbarInfo({
          ...snackbarInfo,
          show: true,
          type: 'error',
          message,
        });
        setFormSubmitted(false);
        formData.current = null;
      });
  };

  const onFormSubmit = (data) => {
    if (
      !isConfirmationPromptOpen &&
      channelData?.current?.videosCount > 0
    ) {
      formData.current = data;
      confirmationMessage.current =
        'Are you sure, You want to proceed? It will remove all the videos assign to this channel';
      formData.current.resetVideos = true;
      setIsConfirmationPromptOpen(true);
      return;
    }
    setFormSubmitted(true);
    setSnackbarInfo({
      ...snackbarInfo,
      show: false,
    });


    const finalData = data || formData.current;
    const payload = {
      title: finalData.name.trim(),
      status: finalData.status,
      description: finalData.description.trim(),
    };
    if (formData.current && formData.current.resetVideos)
      payload.resetVideos = true;
    if (dataId) {
      updateExistingData(payload);
    } else {
      payload.channelCode = createRandomString(16);
      saveNewData(payload);
    }
  };

  useEffect(() => {
    if (!dataId) {
      setLoading(false);
      return;
    }

    viewChannelById(dataId)
      .then((res) => {
        channelData.current = res.data;
        // Fill form values
        setValue('name', res.data?.title || '');
        setValue('status', res.data?.status || 0);
        setValue('description', res.data?.description || '');
        setLoading(false);
      })
      .catch((err) => {
        const message =
          err.response?.data?.message ||
          'Something went wrong, please try again.';

        setSnackbarInfo({
          ...snackbarInfo,
          show: true,
          type: 'error',
          message,
        });
        setLoading(false);
      });
  }, [dataId]);

  return (
    <>
      <Dialog
        open
        aria-labelledby="add-dialog-title"
        aria-describedby="add-dialog-description"
        TransitionComponent={Transition}
        fullWidth
        maxWidth="lg"
      >
        <DialogTitle id="view-dialog-title">{title}</DialogTitle>
        <IconButton
          aria-label="close"
          onClick={onCancel}
          size="small"
          sx={{
            position: 'absolute',
            right: 10,
            top: 15,
            color: (theme) => theme.palette.grey[500],
          }}
        >
          <Iconify icon="ic:outline-close" />
        </IconButton>

        <DialogContent dividers id="add-dialog-description">
          {loading && <Loading />}

          {!loading && (
            <form id="add-edit-form" onSubmit={handleSubmit(onFormSubmit)}>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6} md={6}>
                  <Controller
                    name="name"
                    control={control}
                    rules={{
                      ...validationRules.REQUIRED,
                      ...{ ...maxLengthValidation(255) },
                    }}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        label={`${CONTENT_ALIAS.CHANNEL_SINGULAR} Name`}
                        type="text"
                        error={!!errors.name}
                        helperText={errors?.name?.message || null}
                        fullWidth
                        sx = {{
                          '.MuiInputLabel-root': {
                          color: 'gray',
                          '&.Mui-focused': { color: "#9369a8" } 
                        },
                        '& .MuiOutlinedInput-root': {
                          '&.Mui-focused fieldset': {
                            'border-color': '#9369a8',
                          }
                        }
                      }}
                      />
                    )}
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={6}>
                  <Controller
                    name="status"
                    control={control}
                    rules={validationRules.REQUIRED}
                    render={({ field: { onChange, value } }) => (
                      <Status
                        id="sub_status"
                        name="sub_status"
                        label="Status"
                        defaultValue={value}
                        onChange={onChange}
                        validOptions={[1, 0]}
                        sx={{ width: '100%', '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                          borderColor: '#9369a8',
                      }, }}
                        error={errors?.status?.message || ''}
                      />
                    )}
                  />
                </Grid>
                <Grid item xs={12} sm={12} md={12}>
                  <Controller
                    name="description"
                    control={control}
                    rules={{
                      ...validationRules.REQUIRED,
                      ...{ ...maxLengthValidation(1500) },
                    }}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        label="Description"
                        type="text"
                        error={!!errors.description}
                        helperText={errors?.description?.message || null}
                        fullWidth
                        multiline
                        rows={3}
                        sx = {{
                          '.MuiInputLabel-root': {
                          color: 'gray',
                          '&.Mui-focused': { color: "#9369a8" } 
                        },
                        '& .MuiOutlinedInput-root': {
                          '&.Mui-focused fieldset': {
                            'border-color': '#9369a8',
                          }
                        }
                      }}
                      />
                    )}
                  />
                </Grid>
              </Grid>
            </form>
          )}
        </DialogContent>

        <DialogActions sx={{ justifyContent: 'left', marginLeft: 2 }}>
          <Button
            type="submit"
            form="add-edit-form"
            color="primary"
            variant="contained"
            disabled={loading || formSubmitted}
          >
            Save
          </Button>
          <Button
            color="secondary"
            variant="contained"
            onClick={onCancel}
            disabled={formSubmitted}
          >
            Cancel
          </Button>
        </DialogActions>

        {snackbarInfo.show && (
          <SnackbarInfo
            type={snackbarInfo.type}
            message={snackbarInfo.message}
          />
        )}
      </Dialog>
      {isConfirmationPromptOpen && (
        <ConfirmPopup
          title="Confirm Action"
          message={confirmationMessage.current}
          onCancel={() => setIsConfirmationPromptOpen(false)}
          onSuccess={() => {
            onFormSubmit();
            setIsConfirmationPromptOpen(false);
          }}
          cancelButtonText="No"
          successButtonText="Yes"
        />
      )}
    </>
  );
};

AddEditCategories.propTypes = {
  title: PropTypes.string.isRequired,
  dataId: PropTypes.number.isRequired,
  onCancel: PropTypes.func.isRequired,
  onSuccess: PropTypes.func.isRequired,
};

export default AddEditCategories;
